import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { combineLatestWith, map, Observable, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';
import { filter, tap } from 'rxjs/operators';
import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';

import { CorporateService } from '@fcom/common/services/corporate/corporate.service';
import { Data, DataFilterDateRange, DataFilters, DataResponse, DataRow, FilterTimeUnit } from '@fcom/common/interfaces';
import { profile } from '@fcom/core/selectors';
import { finShare } from '@fcom/rx/operators/finShare';
import { Profile } from '@fcom/core-api/login';
import { MediaQueryService } from '@fcom/common/services';
import { ToggleSwitchStatus } from '@fcom/ui-components/components/toggle-switch';

import { FilterDateRangeKey } from '../../utils/utils';
import { CorporateReportsActions } from '../../store/actions';
import { getDateRange } from './get-date-range';
import { dataFilterDateRangeValue } from './utils';
import { selectReport } from '../../store/selectors/report.selector';
import { BarChartXAxisType } from '../bar-chart/constants';
import { DataViewType, DataViewTypes, getDataViewType } from './view-type';
import { XColumnType } from '../data-table/x-column-type';
import { dataIndex } from './constants';
import { TextToggleButtonProps } from '../toggle-button/toggle-button';

@Component({
  selector: 'fin-corporate-data',
  templateUrl: './corporate-data.component.html',
  styleUrls: ['./corporate-data.component.scss'],
})
export class CorporateDataComponent implements OnInit {
  protected readonly BarChartXAxisType = BarChartXAxisType;
  protected readonly ToggleSwitchStatus = ToggleSwitchStatus;
  protected readonly XColumnType = XColumnType;
  protected readonly dataIndex = dataIndex;
  protected readonly SvgLibraryIcon = SvgLibraryIcon;
  protected readonly DataViewType = DataViewType;
  protected readonly FilterDateRangeKey = FilterDateRangeKey;

  destroyRef = inject(DestroyRef);

  dataFiltersFormGroup: FormGroup<DataFilters>;

  displayMode: DataViewTypes;

  dateRangeTranslations$: Observable<string[]>;

  profile$: Observable<Profile>;

  data$: Observable<DataResponse>;

  error$: Observable<string>;

  isMobile$: Observable<boolean>;

  isEmpty$: Observable<boolean>;

  dateRangeButtons: TextToggleButtonProps[];

  legendText$: Observable<string>;

  constructor(
    private store$: Store,
    private corporateService: CorporateService,
    private currentRoute: ActivatedRoute,
    private mediaQueryService: MediaQueryService
  ) {}

  requestedFilters: string[] = [];

  ngOnInit(): void {
    this.isMobile$ = this.mediaQueryService.isBreakpoint$('mobile');

    this.profile$ = this.store$.pipe(profile(), finShare());

    this.dateRangeButtons = Object.values(DataFilterDateRange).map(
      (value) => ({ name: value, text: value }) as TextToggleButtonProps
    );

    this.dataFiltersFormGroup = new FormGroup<DataFilters>({
      dateFilter: new FormControl<string>(FilterDateRangeKey.ALL),
    });

    this.displayMode = {
      spend: DataViewType.CHART,
      ticketTypes: DataViewType.CHART,
      topTenTrips: DataViewType.CHART,
      passengers: DataViewType.CHART,
    };

    const dateFilter$ = this.dataFiltersFormGroup.controls.dateFilter.valueChanges.pipe(finShare());

    dateFilter$
      .pipe(
        filter((dateFilter) => !this.requestedFilters.includes(dateFilter)),
        tap((dateFilter) => this.requestedFilters.push(dateFilter)),
        map((dateFilter) => ({
          timeUnit: FilterTimeUnit.MONTH,
          dateRange: getDateRange(dataFilterDateRangeValue(dateFilter), new Date()),
          fromSaved: Boolean(this.currentRoute.snapshot.queryParams.fromSaved),
        })),
        switchMap((request) => this.corporateService.getSpend(request)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ clientId, response, error }: Data) => {
        this.store$.dispatch(
          CorporateReportsActions.setReports({
            clientId,
            dateRange: this.dataFiltersFormGroup.controls.dateFilter.value,
            response,
            error,
          })
        );
      });

    this.data$ = dateFilter$.pipe(
      combineLatestWith(this.profile$),
      switchMap(([dateFilter, { corporate }]) => this.store$.pipe(selectReport(corporate.clientId, dateFilter))),
      filter((data) => typeof data !== 'string'),
      map((data) => data as DataResponse),
      takeUntilDestroyed(this.destroyRef)
    );

    this.isEmpty$ = this.data$.pipe(
      map(
        (data: DataResponse) =>
          !data || !data.datasets || data.datasets.flatMap(({ rows }) => rows).every(({ value }) => value === 0)
      ),
      takeUntilDestroyed(this.destroyRef)
    );

    this.error$ = dateFilter$.pipe(
      combineLatestWith(this.profile$),
      switchMap(([dateFilter, { corporate }]) => this.store$.pipe(selectReport(corporate.clientId, dateFilter))),
      filter((data) => typeof data === 'string'),
      map((error) => error as string),
      takeUntilDestroyed(this.destroyRef)
    );

    this.dataFiltersFormGroup.controls.dateFilter.setValue(DataFilterDateRange.LAST_YEAR);
  }

  public tripsDomainFilter({ value }: DataRow): boolean {
    return value > 0;
  }

  public setDisplayMode(key: keyof DataViewTypes, mode: string): void {
    this.displayMode[key] = getDataViewType(mode);
  }

  public setFilter(dateFilter: string): void {
    this.dataFiltersFormGroup.controls.dateFilter.setValue(dataFilterDateRangeValue(dateFilter));
  }
}
